import { api } from '../api/client';

import { API_VIEWS_RECENT_URL, API_VIEWS_URL, isTokenValid } from '../utils';
import { onSessionChange } from '../utils/session';

// Re-renders and quick back-and-forth navigation would otherwise POST the
// same view over and over; one record per target per window is plenty.
const THROTTLE_MS = 60e3;
const lastRecorded = new Map();
// The throttle is per account: the next one's first view of a target the
// last one just opened must still be recorded.
onSessionChange(() => lastRecorded.clear());

// Anything that shows history (today: the rail's Recent section) subscribes
// here, so viewing, forgetting, or clearing updates it without a context or
// a poll. Notifications are a nudge to refetch — never the data itself.
const listeners = new Set();

/**
 * Subscribe to "the history changed"; returns the unsubscribe. The listener
 * is handed `{ targetType }` — the kind of record whose history moved — or
 * `{}` when every kind did (a clear). A surface that shows one kind skips the
 * others: the rail's Recent collections has nothing to refetch when an item
 * is viewed, and the items list records one per selection.
 */
export function subscribeToViewChanges(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function notifyViewChange(change = {}) {
	listeners.forEach(listener => {
		try { listener(change); } catch { /* one bad listener must not stop the rest */ }
	});
}

/**
 * Fire-and-forget: tell the server this user just viewed a collection, card,
 * or item. History is a convenience — a failed record must never surface, so
 * errors are swallowed and callers get nothing back to await.
 */
export function recordView(targetType, targetId) {
	if (!targetType || !targetId || !isTokenValid()) return;
	const key = `${targetType}:${targetId}`;
	const now = Date.now();
	const last = lastRecorded.get(key) || 0;
	if (now - last < THROTTLE_MS) return;
	lastRecorded.set(key, now);
	api.post(API_VIEWS_URL, { targetType, targetId: String(targetId) })
		.then(() => notifyViewChange({ targetType }))
		.catch(() => { lastRecorded.delete(key); });
}

/** Test hook: forget the throttle between test cases. */
export function _resetViewThrottle() {
	lastRecorded.clear();
}

/**
 * The user's history, newest first. Each entry:
 * { targetType, target_id, collection_id, viewedAt, target } — targets the
 * user can no longer read were already dropped by the server.
 */
export async function fetchRecentViews(limit = 20, targetType) {
	const params = targetType ? { limit, targetType } : { limit };
	const { data } = await api.get(API_VIEWS_RECENT_URL, { params });
	return Array.isArray(data?.views) ? data.views : [];
}

/**
 * Forget one entry. Idempotent server-side, so a double-click is harmless.
 * The throttle key goes with it: forgetting something and then opening it
 * again should put it back at the top, not wait out the window.
 */
export async function removeView(targetType, targetId) {
	const id = String(targetId);
	await api.delete(`${API_VIEWS_URL}/${targetType}/${encodeURIComponent(id)}`);
	lastRecorded.delete(`${targetType}:${id}`);
	notifyViewChange({ targetType });
}

/** Clears the caller's entire history. */
export async function clearViews() {
	const response = await api.delete(API_VIEWS_URL);
	lastRecorded.clear();
	notifyViewChange();
	return response;
}
